import {OpeningLogiCtrlStatus} from "../../../../../api/Openings";
import {
    CircularProgress,
    DialogContent, DialogContentText,
    DialogTitle,
    Divider,
    IconButton,
    Tab,
    Tabs,
    Typography
} from "@mui/material";
import {Dialog2} from "../../../../../misc/Dialog2";
import Close from "@mui/icons-material/Close";
import {useContext, useMemo, useState} from "react";
import {useAsync2} from "nate-react-api-helpers";
import {api} from "../../../../../api/API";
import {useProjectId} from "../../ProjectName";
import {OpeningsExistingTab} from "./OpeningsExistingTab";
import {OpeningsRulesTab} from "./OpeningsRulesTab";
import {useBackgroundAction} from "../../../manage/sov/UseBackgroundTask";
import {EditContext} from "../../../../../misc/scroller/EditProvider";
import {Popover} from "../../../../../misc/Popover";

export function LogiCtrlOpeningModal(props: {
    anchorEl: HTMLElement | null;
    onClose: () => void;
    opening: number;
    name: string;
    value: OpeningLogiCtrlStatus;
    onReload?(): void;
    existingOnly?: boolean;
}) {

    const projectId = useProjectId();

    const rulesReq = useAsync2((input) => {
        return api.logiCtrl.listRules(input)
    }, {opening: props.opening, project: projectId}, [props.opening, projectId]);

    const statusReq = useAsync2((input) => {
        return api.logiCtrl.listStatuses(input)
    }, {opening: props.opening, project: projectId}, [props.opening, projectId]);

    const ready = !statusReq.loadingOrError && !rulesReq.loadingOrError;

    const [tab, setTab] = useState<"rules" | "existing">(props.existingOnly ? "existing" : "rules");
    const [requireRecalcOnClose, setRequireRecalcOnClose] = useState(false);

    const recalcAndClose = useBackgroundAction<Parameters<typeof api.logiCtrl.applyExisting>[0], any>({
        callback: async (input) => api.logiCtrl.applyExisting(input),
        onDone: () => {
            props.onReload?.();
            props.onClose();
        }
    });

    const onClose = () => {
        if (requireRecalcOnClose) {
            recalcAndClose.callback({
                project: projectId,
                opening: props.opening
            })

            return;
        }

        props.onClose();
    }

    const existingCount = useMemo(() => {
        return statusReq.asList.filter(v => !v.autoReplace).reduce((sum, s) => sum + (s.removeInvRequests?.length || 0), 0);
    }, [statusReq.asList]);

    const editCtx = useContext(EditContext);

    if(recalcAndClose.LoadingElement) {
        return (
            <Dialog2 open>
                <DialogTitle>Applying Background Changes...</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        This may take a moment.
                    </DialogContentText>

                    {recalcAndClose.LoadingElement}
                </DialogContent>
            </Dialog2>
        )
    }

    return (
        <Popover open anchorEl={props.anchorEl} onClose={onClose}
            slotProps={{paper: {sx: {borderRadius: 2, minWidth: 240}}}}>
            <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 8px 4px 12px",
            }}>
                <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                }}>
                    <Typography variant="subtitle2" color="text.secondary">Logistics Status: {props.name}</Typography>
                    {requireRecalcOnClose && <span style={{
                        width: 6, height: 6, borderRadius: "50%",
                        backgroundColor: "#1976d2", display: "inline-block",
                    }} />}
                    {!ready && <CircularProgress size="1em" />}
                </div>
                <IconButton size="small" onClick={onClose} sx={{padding: "2px"}}>
                    <Close sx={{fontSize: 16}} />
                </IconButton>
            </div>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{minHeight: 0, px: 1}}>
                {!props.existingOnly && <Tab label="Rules" value="rules" sx={{minHeight: 0, py: 0.5, textTransform: "none", fontSize: 12}} />}
                <Tab label={`Existing (${existingCount})`} value="existing" sx={{minHeight: 0, py: 0.5, textTransform: "none", fontSize: 12}} />
            </Tabs>
            <Divider />
            {statusReq.error ? statusReq.LoadingOrErrorElement : null}
            {rulesReq.error ? rulesReq.LoadingOrErrorElement : null}
            {tab === "rules" && (
                <OpeningsRulesTab
                    opening={props.opening}
                    value={props.value}
                    onClose={onClose}
                    onReload={props.onReload}
                    ready={ready}
                    rules={rulesReq.asList}
                    statuses={statusReq.asList}
                />
            )}
            {tab === "existing" && (
                <OpeningsExistingTab
                    opening={props.opening}
                    project={projectId}
                    onNeedsRecalc={setRequireRecalcOnClose}
                    onReload={() => {
                        rulesReq.reload();
                        statusReq.reload();
                        props.onReload?.();

                        try {
                            editCtx.reload();
                        } catch (e) {}
                    }} />
            )}
        </Popover>
    )
}
